export const META_PIXEL_ID = '1366878365513025'

type FbqParams = Record<string, string | number | boolean>

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void
      queue?: unknown[][]
      push?: unknown
      loaded?: boolean
      version?: string
    }
    _fbq?: unknown
    __fcsPixel?: {
      initialized: boolean
      fired: Record<string, number>
    }
  }
}

/**
 * Deduplication state lives on `window` (not in module scope) so a re-mount,
 * hot reload or a second import of this module cannot re-fire the same event.
 */
function state() {
  if (!window.__fcsPixel) {
    window.__fcsPixel = { initialized: false, fired: {} }
  }
  return window.__fcsPixel
}

function eventId(event: string): string {
  return `${event}-${META_PIXEL_ID}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 10)}`
}

/**
 * Loads the Meta Pixel base code and fires PageView — exactly once per page
 * load, regardless of how many times this is called.
 */
export function initMetaPixel(): void {
  if (typeof window === 'undefined') return
  const pixel = state()
  if (pixel.initialized) return
  pixel.initialized = true

  if (!window.fbq) {
    const fbq: Window['fbq'] = function (...args: unknown[]) {
      if (fbq!.callMethod) {
        fbq!.callMethod(...args)
      } else {
        fbq!.queue!.push(args)
      }
    } as NonNullable<Window['fbq']>

    fbq.queue = []
    fbq.loaded = true
    fbq.version = '2.0'
    fbq.push = fbq

    window.fbq = fbq
    window._fbq = fbq

    const script = document.createElement('script')
    script.async = true
    script.src = 'https://connect.facebook.net/en_US/fbevents.js'
    document.head.appendChild(script)
  }

  // `init` is only ever called once — a second init on the same ID is what
  // makes Events Manager report duplicated PageViews.
  window.fbq?.('init', META_PIXEL_ID)
  trackPixelEventOnce('PageView')
}

/** Fires a standard event with a unique eventID (browser/CAPI deduplication). */
export function trackPixelEvent(event: string, params?: FbqParams): void {
  if (typeof window === 'undefined' || !window.fbq) return
  window.fbq('track', event, params ?? {}, { eventID: eventId(event) })
}

/** Fires a standard event at most once per page load. */
export function trackPixelEventOnce(event: string, params?: FbqParams): void {
  if (typeof window === 'undefined') return
  const pixel = state()
  if (pixel.fired[event]) return
  pixel.fired[event] = Date.now()
  trackPixelEvent(event, params)
}

/**
 * Fires a standard event, ignoring repeats inside `windowMs`.
 * Used for click events so a double-tap does not send two events.
 */
export function trackPixelEventThrottled(
  event: string,
  params?: FbqParams,
  windowMs = 3000,
): void {
  if (typeof window === 'undefined') return
  const pixel = state()
  const last = pixel.fired[event] ?? 0
  if (Date.now() - last < windowMs) return
  pixel.fired[event] = Date.now()
  trackPixelEvent(event, params)
}

export const PRODUCT_EVENT_PARAMS = {
  content_name: 'Functional Coach System',
  content_type: 'product',
  content_ids: 'functional-coach-system',
  value: 27,
  currency: 'USD',
} as const
